import { useEffect, useRef, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";

import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import { DataTable } from "@/components/table/DataTable";
import type { DataTableColumn } from "@/components/table/columns";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Input } from "@/components/ui/Input";
import { Popover } from "@/components/ui/Popover";
import { ApiError, apiFetch } from "@/lib/api";
import type { Page, ReferenceListItemResource } from "@/lib/api-types";
import { useIsOwner } from "@/lib/current-actor";
import { cx } from "@/lib/cx";

/**
 * `/settings/reference-lists` (Story 5.2, AD-28, spec judgement call 8):
 * one generic, `list_key`-parameterized admin screen — a `DataTable`
 * (Epic 2's shared table component, exactly as every other list screen in
 * this codebase already uses it) with add/remove affordances, never four
 * bespoke screens. The tab row below switches which live `list_key` this
 * one screen's `DataTable`/add-form pair currently reads and writes; the
 * component underneath never branches on which one is selected beyond that
 * single piece of state (the same "configured by data, not by a route-
 * specific branch" discipline `DataTable`/`RecordPage` themselves already
 * follow, VC-2.2-01's own rule applied one layer up).
 *
 * `GET /reference-list-items` is `role=Role.READ_ONLY` — every account
 * reads every list; `POST`/`DELETE` are `role=Role.OWNER` — the add/remove
 * affordances are withheld entirely (`useIsOwner`, never merely disabled)
 * for anyone but a confirmed Owner, mirroring `AlertsList.tsx`'s identical
 * Owner-only-affordance pattern. "Remove" is a real soft delete through the
 * standard repository path (never a hard delete), the same `ConfirmDialog`
 * shape `UsersList.tsx`'s own row-removal flow already established.
 */

//: The four Phase-1a reference lists this story seeds (spec judgement call
//: 5) — a fixed, small navigation list for *this screen's own tabs*, not a
//: hardcoded copy of any list's own entries (VC-5.2-08 forbids hardcoding a
//: reference list's own seeded value/label pairs; the four `list_key`
//: identifiers themselves are structural navigation, the same way route
//: paths are).
//: A later epic's own reference list is reached by passing its `list_key`
//: through this same mechanism — no new component, only a new tab. Story
//: 16.2 adds the fifth: `vendor_trade` (AD-28, seeded by Story 16.1's own
//: migration) — the identical one-line addition `document_category`'s own
//: precedent already established, so an Owner can manage the trade
//: vocabulary (electrician, plumber, HVAC, ...) themselves.
const REFERENCE_LISTS: { key: string; label: string }[] = [
  { key: "document_category", label: "Document categories" },
  { key: "property_type", label: "Property types" },
  { key: "unit_status", label: "Unit statuses" },
  { key: "notice_type", label: "Notice types" },
  { key: "vendor_trade", label: "Vendor trades" },
];

export function ReferenceLists() {
  const [listKey, setListKey] = useState(REFERENCE_LISTS[0]!.key);
  const isOwner = useIsOwner();
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [newValue, setNewValue] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [addError, setAddError] = useState<string | undefined>(undefined);
  const [removeTarget, setRemoveTarget] = useState<ReferenceListItemResource | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | undefined>(undefined);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    document.title = "Reference lists — PropDesk";
  }, []);

  const listQueryKey = ["reference-list-items", listKey] as const;
  const listQuery = useQuery({
    queryKey: listQueryKey,
    queryFn: () => apiFetch<Page<ReferenceListItemResource>>(`/reference-list-items?list_key=${encodeURIComponent(listKey)}`),
    placeholderData: keepPreviousData,
  });

  function startAdding() {
    setIsAdding(true);
    setNewValue("");
    setNewLabel("");
    setAddError(undefined);
  }

  function cancelAdding() {
    setIsAdding(false);
    setAddError(undefined);
  }

  async function handleAdd() {
    const trimmedValue = newValue.trim();
    const trimmedLabel = newLabel.trim();
    if (trimmedValue === "" || trimmedLabel === "") {
      setAddError("Enter both a value and a label.");
      return;
    }
    const existing = listQuery.data?.data ?? [];
    const nextSortOrder = existing.reduce((max, item) => Math.max(max, item.sort_order), -1) + 1;
    try {
      const created = await apiFetch<ReferenceListItemResource>("/reference-list-items", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          list_key: listKey,
          value: trimmedValue,
          label: trimmedLabel,
          sort_order: nextSortOrder,
        }),
      });
      queryClient.setQueryData<Page<ReferenceListItemResource>>(listQueryKey, (current) =>
        current
          ? { data: [...current.data, created], meta: { ...current.meta, total: current.meta.total + 1 } }
          : { data: [created], meta: { total: 1, page: 1, page_size: 1 } },
      );
      cancelAdding();
    } catch (error) {
      setAddError(error instanceof ApiError ? error.message : "Something went wrong. Please try again.");
    }
  }

  async function confirmRemove() {
    if (removeTarget === null) {
      return;
    }
    setIsRemoving(true);
    setRemoveError(undefined);
    try {
      await apiFetch<{ deleted: boolean }>(`/reference-list-items/${removeTarget.id}`, { method: "DELETE" });
      queryClient.setQueryData<Page<ReferenceListItemResource>>(listQueryKey, (current) =>
        current === undefined
          ? current
          : {
              ...current,
              data: current.data.filter((item) => item.id !== removeTarget.id),
              meta: { ...current.meta, total: Math.max(0, current.meta.total - 1) },
            },
      );
      setRemoveTarget(null);
    } catch (error) {
      setRemoveError(error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.");
    } finally {
      setIsRemoving(false);
    }
  }

  const columns: DataTableColumn<ReferenceListItemResource>[] = [
    { key: "label", sortable: false },
    { key: "value", sortable: false },
  ];

  const errorMessage = listQuery.error instanceof ApiError ? listQuery.error.message : undefined;
  const activeList = REFERENCE_LISTS.find((entry) => entry.key === listKey) ?? REFERENCE_LISTS[0]!;

  return (
    <div className="mx-auto max-w-content-max">
      <ReadOnlyNotice className="mb-4" />
      <h1 ref={headingRef} tabIndex={-1} className="text-title font-semibold tracking-title text-ink md:hidden">
        Reference lists
      </h1>
      <p className="mb-6 mt-2 text-body text-ink-secondary">
        The fixed-choice lists PropDesk offers throughout the app. Add or remove an entry here and it
        takes effect everywhere immediately — no developer needed.
      </p>

      <div role="tablist" aria-label="Reference lists" className="mb-4 flex flex-wrap gap-2">
        {REFERENCE_LISTS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            role="tab"
            aria-selected={entry.key === listKey}
            onClick={() => setListKey(entry.key)}
            className={cx(
              "inline-flex h-tap-target-min items-center rounded-md border px-4 text-button",
              entry.key === listKey
                ? "border-accent bg-accent-quiet text-accent"
                : "border-border-strong bg-canvas text-ink hover:bg-surface-hover",
            )}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <DataTable
        caption={activeList.label}
        itemNamePlural="entries"
        columns={columns}
        page={listQuery.data}
        isPending={listQuery.isPending}
        isFetching={listQuery.isFetching}
        isError={listQuery.isError}
        {...(errorMessage ? { errorMessage } : {})}
        getRowHref={() => "/settings/reference-lists"}
        emptyState={{
          heading: "Nothing in this list yet.",
          body: "Add the first entry below.",
        }}
        {...(isOwner
          ? {
              rowActions: (row: ReferenceListItemResource) => (
                <Button
                  type="button"
                  variant="ghost"
                  aria-label={`Remove ${row.label}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    setRemoveTarget(row);
                  }}
                >
                  Remove
                </Button>
              ),
            }
          : {})}
      />

      {isOwner ? (
        <div className="mt-4">
          <Popover
            open={isAdding}
            onOpenChange={(open) => (open ? startAdding() : cancelAdding())}
            trigger={
              <Button type="button" variant="ghost">
                Add an entry
              </Button>
            }
          >
            <form
              className="flex w-64 flex-col gap-3"
              onSubmit={(event) => {
                event.preventDefault();
                void handleAdd();
              }}
            >
              <label className="flex flex-col gap-1">
                <span className="text-label font-medium text-ink-secondary">Label</span>
                <Input autoFocus value={newLabel} onChange={(event) => setNewLabel(event.target.value)} />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-label font-medium text-ink-secondary">Value</span>
                <Input value={newValue} onChange={(event) => setNewValue(event.target.value)} />
              </label>
              {addError ? (
                <p role="alert" className="text-meta text-danger">
                  {addError}
                </p>
              ) : null}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={cancelAdding}>
                  Cancel
                </Button>
                <Button type="submit" variant="secondary">
                  Add
                </Button>
              </div>
            </form>
          </Popover>
        </div>
      ) : null}

      {removeTarget ? (
        <ConfirmDialog
          open={removeTarget !== null}
          onOpenChange={(open) => {
            if (!open) {
              setRemoveTarget(null);
              setRemoveError(undefined);
            }
          }}
          title="Remove this entry?"
          description={`Removing "${removeTarget.label}" takes it out of this list immediately. It can be restored by a developer if needed, but there is no undo in the interface.`}
          confirmLabel={`Remove ${removeTarget.label}`}
          confirmingLabel="Removing…"
          isConfirming={isRemoving}
          onConfirm={() => void confirmRemove()}
          fallbackFocusRef={headingRef}
          {...(removeError ? { errorMessage: removeError } : {})}
        />
      ) : null}
    </div>
  );
}

export { REFERENCE_LISTS };
